<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";

import { Button, Checkbox, FormAlert, Input, Label } from "@/shared/ui";

import { useLoginForm } from "../model/useLoginForm";

const { t } = useI18n();
const {
  email,
  password,
  rememberMe,
  submitting,
  formError,
  fieldErrors,
  submit,
} = useLoginForm();

// FormAlert — компонент; його корінь (role=alert, tabindex=-1) фокусуємо через $el.
const alertRef = ref<{ $el: HTMLElement } | null>(null);

function focusById(id: string): void {
  document.getElementById(id)?.focus();
}

// Focus (§4.7): mount → email (або password, якщо email префілл).
onMounted(() => {
  focusById(email.value ? "login-password" : "login-email");
});

// Помилка форми → фокус на alert (assertive); field-invalid → перше невалідне поле.
watch(formError, async (val) => {
  if (!val) {
    return;
  }
  await nextTick();
  alertRef.value?.$el.focus();
});

watch(
  () => [fieldErrors.email, fieldErrors.password] as const,
  async ([emailErr, passwordErr]) => {
    if (formError.value || (!emailErr && !passwordErr)) {
      return;
    }
    await nextTick();
    focusById(emailErr ? "login-email" : "login-password");
  },
);
</script>

<template>
  <form
    class="flex flex-col gap-5"
    novalidate
    @submit.prevent="submit"
  >
    <FormAlert
      v-if="formError"
      ref="alertRef"
      variant="destructive"
      :message="formError"
    />

    <div class="flex flex-col gap-2">
      <Label for="login-email">{{ t("auth.fields.email") }}</Label>
      <Input
        id="login-email"
        v-model="email"
        type="email"
        name="email"
        autocomplete="username"
        inputmode="email"
        :placeholder="t('auth.fields.emailPlaceholder')"
        :invalid="!!fieldErrors.email"
        :aria-describedby="fieldErrors.email ? 'login-email-error' : undefined"
        :disabled="submitting"
      />
      <p
        v-if="fieldErrors.email"
        id="login-email-error"
        class="text-sm text-destructive"
      >
        {{ fieldErrors.email }}
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <Label for="login-password">{{ t("auth.fields.password") }}</Label>
      <Input
        id="login-password"
        v-model="password"
        type="password"
        name="password"
        autocomplete="current-password"
        :placeholder="t('auth.fields.passwordPlaceholder')"
        :invalid="!!fieldErrors.password"
        :aria-describedby="fieldErrors.password ? 'login-password-error' : undefined"
        :disabled="submitting"
      />
      <p
        v-if="fieldErrors.password"
        id="login-password-error"
        class="text-sm text-destructive"
      >
        {{ fieldErrors.password }}
      </p>
    </div>

    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2">
        <Checkbox
          id="login-remember"
          v-model="rememberMe"
          :disabled="submitting"
        />
        <Label
          for="login-remember"
          class="font-normal"
        >
          {{ t("auth.fields.remember") }}
        </Label>
      </div>
      <RouterLink
        to="/forgot-password"
        class="rounded-sm text-sm font-medium text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        {{ t("auth.login.forgotPassword") }}
      </RouterLink>
    </div>

    <Button
      type="submit"
      class="w-full"
      :loading="submitting"
    >
      {{ t("auth.login.submit") }}
    </Button>
  </form>
</template>
